/**
 * preDeploymentRetryApi — retry a gate-failed pre-deployment run through the
 * eval API (EvaluationManager online/service/evaluation_retry_routes.py):
 *
 *   POST /pre-deployment-runs/{runId}/retry[?dry_run=true]
 *
 * dryRun previews the per-target plan and writes nothing; otherwise the
 * retryable targets are queued as ONE pending batch and a sweep is kicked off.
 * Per target the backend decides RETRY (staging unchanged since the failure),
 * SUPERSEDED (a newer commit landed — it gets its own evaluation) or IN_FLIGHT
 * (already queued / being evaluated), so a retry never overrides newer work.
 *
 * Uses EvalApiClient (SigV4 + X-Id-Token, rocky-admin gate) rather than the
 * Smithy client: the route lives in the Python eval API, next to the
 * aggregation code it writes into. snake_case → camelCase at this boundary,
 * like preDeploymentApi.
 */
import { evalApi } from "../../../client/EvalApiClient";

export type RetryDecision = "RETRY" | "SUPERSEDED" | "IN_FLIGHT";

export interface RetryPlanItem {
  category: string;
  agentName: string;
  name: string;
  decision: RetryDecision;
  reason: string;
}

export interface RetryResult {
  runId: string;
  dryRun: boolean;
  /** True only when a retry batch was actually written. */
  queued: boolean;
  /** False ⇒ the hourly cron starts the retry instead of right now. */
  sweepTriggered: boolean;
  items: RetryPlanItem[];
}

/** A failed retry call. 409s carry a machine `reason` — NOT_CHANGE_SET,
 *  INTERRUPTED, NOT_COMPLETE, PASSED or ALREADY_QUEUED. */
export class RetryError extends Error {
  readonly status?: number;
  readonly reason?: string;

  constructor(status: number | undefined, reason: string | undefined, message: string) {
    super(message);
    this.name = "RetryError";
    this.status = status;
    this.reason = reason;
  }
}

// EvalApiClient.signedRequest throws `[EvalApiClient] HTTP <status>: <body>`.
const HTTP_ERROR_RE = /HTTP (\d{3}): ([\s\S]*)$/;

/** Recover status / reason / message from a signedRequest failure. */
export function toRetryError(e: unknown): RetryError {
  const text = e instanceof Error ? e.message : String(e);
  const match = HTTP_ERROR_RE.exec(text);
  if (!match) return new RetryError(undefined, undefined, text);
  let body: { reason?: string; message?: string } = {};
  try {
    body = JSON.parse(match[2]);
  } catch {
    // Non-JSON body (e.g. an API Gateway 5xx page) — surface the raw text.
  }
  return new RetryError(Number(match[1]), body.reason, body.message || match[2] || text);
}

function toRetryResult(raw: any): RetryResult {
  return {
    runId: raw?.run_id ?? "",
    dryRun: Boolean(raw?.dry_run),
    queued: Boolean(raw?.queued),
    sweepTriggered: Boolean(raw?.sweep_triggered),
    items: (raw?.items ?? []).map((item: any) => ({
      category: item.category ?? "",
      agentName: item.agent_name ?? "",
      name: item.name ?? "",
      decision: item.decision,
      reason: item.reason ?? "",
    })),
  };
}

/** Preview (dryRun) or queue a retry of a gate-failed run; throws RetryError. */
export async function retryRun(runId: string, dryRun: boolean): Promise<RetryResult> {
  const path = `/pre-deployment-runs/${encodeURIComponent(runId)}/retry${dryRun ? "?dry_run=true" : ""}`;
  try {
    return toRetryResult(await evalApi.signedRequest("POST", path));
  } catch (e) {
    throw toRetryError(e);
  }
}
